"use client";

import { cn } from "@/lib/utils";
import { FC, Fragment, useMemo, useRef, useEffect, useState } from "react";
import { z } from "zod";
import Image from "next/image";
import { useDispatch } from "react-redux";
import { formatBackgroundStyle } from "@/utils/styles/format";
import { LEAD_DROP_VARIANTS } from "@/constants/leadConstants";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
import { leadCtaClick } from "@/customAnalytics/trackingEvents";
import { useIsMobile } from "@/hooks/useIsMobile";
import RevealOnScroll from "@/components/atoms/RevealOnScroll";
import { hexToRgba } from "@/utils/utils";

/** A single comparison row (criteria + 3 columns), stored in the `metadata` JSON array. */
const RowSchema = z.object({
  id: z.union([z.number(), z.string()]),
  meta_criteria_name: z.string().optional().default(""),
  meta_criteria_text_color: z.string().optional().default("#FFFFFF"),
  meta_criteria_background_color: z
    .string()
    .optional()
    .default("rgba(255,255,255,0.1)"),

  meta_column1_text: z.string().optional().default(""),
  meta_column1_text_color: z.string().optional().default(""),
  meta_column1_image: z.string().optional().default(""),
  meta_column1_alt_image: z.string().optional().default(""),
  meta_column1_background_color: z
    .string()
    .optional()
    .default("rgba(2,167,182,0.2)"),

  meta_column2_text: z.string().optional().default(""),
  meta_column2_text_color: z.string().optional().default(""),
  meta_column2_image: z.string().optional().default(""),
  meta_column2_alt_image: z.string().optional().default(""),
  meta_column2_background_color: z.string().optional().default(""),

  meta_column3_text: z.string().optional().default(""),
  meta_column3_text_color: z.string().optional().default(""),
  meta_column3_image: z.string().optional().default(""),
  meta_column3_alt_image: z.string().optional().default(""),
  meta_column3_background_color: z.string().optional().default(""),

  meta_priority: z.coerce.number().optional().default(0),
  /** MySQL/API may send 0/1; treat truthy as active. */
  meta_status: z
    .preprocess(
      (v) => !(v === false || v === 0 || v === "0"),
      z.boolean(),
    )
    .optional()
    .default(true),
});

const Comparison1DataSchema = z.object({
  desktop_background_color: z.string().optional().default(""),
  mobile_background_color: z.string().optional().default(""),
  backvariant: z
    .enum(["variant_1", "variant_2", "variant_3"])
    .optional()
    .default("variant_1"),
  gradient_color_1: z.string().optional().default(""),
  gradient_color_2: z.string().optional().default(""),
  gradient_color_3: z.string().optional().default(""),
  gradient_color_4: z.string().optional().default(""),

  pre_title: z.string().optional().default(""),
  pre_title_color: z.string().optional().default("rgba(255,255,255,0.8)"),
  title: z.string().optional().default(""),
  title_start_color: z.string().optional().default(""),
  title_end_color: z.string().optional().default(""),
  title_gradient_angle: z.string().optional().default("90deg"),
  description: z.string().optional().default(""),
  description_text_color: z.string().optional().default("rgba(255,255,255,0.7)"),

  column1: z.string().optional().default(""),
  column1_text_color: z.string().optional().default("#000000"),
  column1_background_color: z.string().optional().default("#02A7B6"),
  column2: z.string().optional().default(""),
  column2_text_color: z.string().optional().default("#FFFFFF"),
  column2_background_color: z.string().optional().default(""),
  column3: z.string().optional().default(""),
  column3_text_color: z.string().optional().default("#FFFFFF"),
  column3_background_color: z.string().optional().default(""),

  cta_text: z.string().optional().default(""),
  cta_text_color: z.string().optional().default("#222222"),
  cta_background_color: z.string().optional().default("#FFFFFF"),

  metadata: z.array(RowSchema).optional().default([]),
  enable_animation: z.union([z.boolean(), z.number()]).optional().default(false),
});

type ComparisonRow = z.infer<typeof RowSchema>;
type Comparison1Data = z.infer<typeof Comparison1DataSchema>;

/**
 * Data column width. On mobile the columns flex to fill the row, so fewer
 * visible columns each take more width; from `sm` up they use fixed widths.
 */
const DATA_COL =
  "flex-1 min-w-[64px] sm:flex-none sm:w-[180px] md:w-[220px] lg:w-[270px] xl:w-[300px]";
/** Cell height (Figma: 56px). min-h so the criteria text can wrap on narrow widths. */
const CELL_H = "min-h-[56px]";

/** Content-length caps from the design spec (criteria 35, column header 25, cell value 12). */
const MAX_CRITERIA_CHARS = 35;
const MAX_COLUMN_HEADER_CHARS = 25;
const MAX_VALUE_CHARS = 12;

/** Hard-cap displayed text length, appending an ellipsis when the source exceeds `max`. */
const clampChars = (text: string | undefined, max: number): string => {
  const t = text ?? "";
  return t.length > max ? `${t.slice(0, max).trimEnd()}…` : t;
};

/** One data cell: optional icon image + optional text, centered. */
const Cell: FC<{
  text?: string;
  textColor?: string;
  image?: string;
  alt?: string;
  bgColor?: string;
  highlighted?: boolean;
  accentColor?: string;
  isLast?: boolean;
}> = ({ text, textColor, image, alt, bgColor, highlighted, accentColor, isLast }) => (
  <div
    className={cn(
      DATA_COL,
      CELL_H,
      "flex flex-col sm:flex-row items-center justify-center gap-[4px] px-[4px] sm:gap-[8px] sm:px-[8px]",
      highlighted && "border-l border-r border-solid",
      highlighted && isLast && "border-b",
    )}
    style={{
      backgroundColor: bgColor ? hexToRgba(bgColor, 0.2) : undefined,
      borderColor: highlighted ? accentColor : undefined,
    }}
  >
    {image ? (
      <Image
        src={image}
        alt={alt || text || ""}
        width={24}
        height={24}
        className={cn(
          "w-[18px] h-[18px] sm:w-[24px] sm:h-[24px] object-contain shrink-0",
          text ? "w-[14px] h-[14px] sm:w-[24px] sm:h-[24px]" : ""
        )}
      />
    ) : null}
    {text ? (
      <p
        className={cn(
          "leading-normal text-center",
          "text-[8px] sm:text-[12px] md:text-[14px] lg:text-[16px]",
        )}
        style={{
          fontFamily: "var(--font-google-sans)",
          color: textColor || "rgba(255,255,255,0.7)",
        }}
      >
        {text}
      </p>
    ) : null}
  </div>
);

export const Comparison1: FC<any> = (props) => {
  const actualData = props?.data?.data || props?.data || props;
  const dispatch = useDispatch();
  const sectionRef = useRef<HTMLElement>(null);
  const isMobile = useIsMobile();
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  const parsedResult = useMemo(
    () => Comparison1DataSchema.safeParse(actualData),
    [actualData],
  );

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (sectionRef.current) {
  //     FireWidgetViewedEvent(sectionRef, "comparison");
  //   }
  // }, []);

  const parsedData = useMemo<Comparison1Data | null>(
    () => (parsedResult.success ? parsedResult.data : null),
    [parsedResult],
  );

  const rows = useMemo<ComparisonRow[]>(
    () =>
      parsedData
        ? [...parsedData.metadata]
            .filter((r) => r.meta_status)
            .sort((a, b) => a.meta_priority - b.meta_priority)
        : [],
    [parsedData],
  );

  if (!parsedResult.success || !parsedData) {
    console.error("Comparison1 validation failed", parsedResult.error);
    return null;
  }

  const bgColor = formatBackgroundStyle(parsedData, mounted ? Boolean(isMobile) : false, "#000000");
  const accent = parsedData.column1_background_color || "#02A7B6";
  const isAnimationEnabled =
    parsedData.enable_animation === true || parsedData.enable_animation === 1;

  const titleGradient =
    parsedData.title_start_color && parsedData.title_end_color
      ? `linear-gradient(${parsedData.title_gradient_angle || "90deg"}, ${parsedData.title_start_color}, ${parsedData.title_end_color})`
      : undefined;

  const handleCtaClick = () => {
    leadCtaClick({
      section: "comparison",
      component: "cta_button",
      label: parsedData.cta_text,
    });
    dispatch(handleOpenLeaddrop({ variant: LEAD_DROP_VARIANTS.REGISTER }));
  };

  // Comparison columns (up to 3). A column is hidden entirely when its header
  // AND every row's value (text + image) are empty.
  const columnDefs = [
    {
      header: parsedData.column1,
      headerColor: parsedData.column1_text_color,
      headerBg: parsedData.column1_background_color,
      highlighted: true,
      cell: (row: ComparisonRow) => ({
        text: row.meta_column1_text,
        textColor: row.meta_column1_text_color,
        image: row.meta_column1_image,
        alt: row.meta_column1_alt_image,
        bgColor: row.meta_column1_background_color,
      }),
    },
    {
      header: parsedData.column2,
      headerColor: parsedData.column2_text_color,
      headerBg: parsedData.column2_background_color,
      highlighted: false,
      cell: (row: ComparisonRow) => ({
        text: row.meta_column2_text,
        textColor: row.meta_column2_text_color,
        image: row.meta_column2_image,
        alt: row.meta_column2_alt_image,
        bgColor: row.meta_column2_background_color,
      }),
    },
    {
      header: parsedData.column3,
      headerColor: parsedData.column3_text_color,
      headerBg: parsedData.column3_background_color,
      highlighted: false,
      cell: (row: ComparisonRow) => ({
        text: row.meta_column3_text,
        textColor: row.meta_column3_text_color,
        image: row.meta_column3_image,
        alt: row.meta_column3_alt_image,
        bgColor: row.meta_column3_background_color,
      }),
    },
  ];

  const visibleColumns = columnDefs.filter((col) => {
    const hasHeader = !!col.header?.trim();
    const hasValue = rows.some((r) => {
      const c = col.cell(r);
      return !!c.text?.trim() || !!c.image?.trim();
    });
    return hasHeader || hasValue;
  });

  return (
    <section
      ref={sectionRef}
      className={cn(
        "relative w-full overflow-hidden flex justify-center",
        "xs:px-[20px] xs:py-[40px]",
        "sm:px-[60px] sm:py-[40px]",
        "md:px-[80px] md:py-[60px]",
        "lg:px-[80px] lg:py-[60px]",
        "xl:px-[140px] xl:py-[80px]",
      )}
      style={bgColor}
    >
      <div
        className={cn(
          "relative z-10 w-full",
          "max-w-[354px]",
          "sm:max-w-[960px]",
          "md:max-w-[1110px]",
          "lg:max-w-[1270px]",
          "xl:max-w-[1470px]",
        )}
      >
        {/* Title block */}
        <div className="flex flex-col mb-[24px] lg:mb-[36px]">
          {parsedData.pre_title ? (
            // <RevealOnScroll>
              <p
                className="font-semibold leading-normal white-title-wf"
                style={{
                  fontFamily: "var(--font-google-sans)",
                  color: parsedData.pre_title_color,
                }}
              >
                {parsedData.pre_title}
              </p>
            // </RevealOnScroll>
          ) : null}

          {parsedData.title ? (
            // <RevealOnScroll>
              <p
                className="font-bold leading-[1.2] bg-clip-text gradient-title-denton line-clamp-2"
                style={{
                  backgroundImage: titleGradient,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  fontFamily: "var(--font-merriweather)",
                }}
              >
                {parsedData.title}
              </p>
            // </RevealOnScroll>
          ) : null}

          {parsedData.description ? (
            // <RevealOnScroll>
              <p
                className={cn(
                  "font-semibold leading-[1.49] mt-[16px] line-clamp-2",
                  "text-[12px] sm:text-[14px] md:text-[16px] lg:text-[16px] xl:text-[18px]",
                )}
                style={{
                  fontFamily: "var(--font-google-sans)",
                  color: parsedData.description_text_color,
                }}
              >
                {parsedData.description}
              </p>
            // </RevealOnScroll>
          ) : null}
        </div>

        {/* Table — horizontally scrollable on mobile when it can't fit */}
        {rows.length > 0 ? (
          <div className="w-full overflow-x-auto hide-scrollbar">
            <div className="flex flex-col gap-[2px] min-w-0">
              {/* Header row */}
              <div className="flex items-stretch">
                {/* Empty criteria header */}
                <div className="flex-[1.5] sm:flex-1 min-w-[92px] sm:min-w-[180px] lg:min-w-[260px]" />
                <div className="w-px self-center h-[32px] bg-[rgba(255,255,255,0.15)]" />
                {visibleColumns.map((col, i) => (
                  <Fragment key={i}>
                    {i > 0 && <div className="w-px self-center h-[32px] bg-[rgba(255,255,255,0.15)]" />}
                    <div
                      className={cn(
                        DATA_COL,
                        "h-[56px] flex items-center justify-center text-center px-[4px] sm:px-[8px]",
                        col.highlighted && "rounded-t-[20px]",
                      )}
                      style={{
                        backgroundColor: col.headerBg || undefined,
                      }}
                    >
                      <p
                        className={cn(
                          "font-bold leading-[1.2] tracking-[0.54px]",
                          "text-[10px] sm:text-[14px] md:text-[16px] lg:text-[18px]",
                        )}
                        style={{
                          fontFamily: "var(--font-merriweather)",
                          color: col.headerColor,
                        }}
                      >
                        {clampChars(col.header, MAX_COLUMN_HEADER_CHARS)}
                      </p>
                    </div>
                  </Fragment>
                ))}
              </div>

              {/* Data rows */}
              {rows.map((row, idx) => {
                const isLast = idx === rows.length - 1;
                return (
                  <RevealOnScroll key={row.id} y={-100} delay={idx * 100} duration={400} enableAnimation={isAnimationEnabled}>

                  <div
                    key={row.id}
                    className="flex items-stretch"
                    style={{ backgroundColor: hexToRgba(row.meta_criteria_background_color, 0.1) }}
                  >
                    {/* Criteria name */}
                    <div
                      className={cn(
                        "flex-[1.5] sm:flex-1 min-w-[92px] sm:min-w-[180px] lg:min-w-[260px] flex items-center",
                        CELL_H,
                        "py-[8px] pr-[6px] sm:pr-[12px]",
                      )}
                    >
                      <p
                        className={cn(
                          "font-medium leading-normal pl-[10px] sm:pl-[24px]",
                          "text-[11px] sm:text-[14px] md:text-[16px] lg:text-[18px]",
                        )}
                        style={{
                          fontFamily: "var(--font-google-sans)",
                          color: row.meta_criteria_text_color,
                        }}
                      >
                        {clampChars(row.meta_criteria_name, MAX_CRITERIA_CHARS)}
                      </p>
                    </div>

                    <div className="w-px self-center h-[32px] bg-[rgba(255,255,255,0.15)]" />

                    {visibleColumns.map((col, ci) => {
                      const c = col.cell(row);
                      return (
                        <Fragment key={ci}>
                          {ci > 0 && <div className="w-px self-center h-[32px] bg-[rgba(255,255,255,0.15)]" />}
                          <Cell
                            text={clampChars(c.text, MAX_VALUE_CHARS)}
                            textColor={c.textColor}
                            image={c.image}
                            alt={c.alt}
                            bgColor={c.bgColor}
                            highlighted={col.highlighted}
                            accentColor={col.highlighted ? accent : undefined}
                            isLast={isLast}
                          />
                        </Fragment>
                      );
                    })}
                  </div>
                  </RevealOnScroll>
                );
              })}
            </div>
          </div>
        ) : null}

        {/* CTA */}
        {parsedData.cta_text ? (
          <RevealOnScroll enableAnimation={isAnimationEnabled}>
          <div className="relative flex justify-center mt-[40px] lg:mt-[48px]">
            {/* <RevealOnScroll> */}
              <button
                type="button"
                data-cta-name="apply_now"
                onClick={handleCtaClick}
                className={cn(
                  "btn-base w-full sm:w-auto text-[16px]",
                  "px-[24px] py-[16px]",
                  "sm:px-[20px] sm:py-[14px]",
                )}
                style={{
                  fontFamily: "var(--font-google-sans)",
                  backgroundColor: parsedData.cta_background_color,
                  color: parsedData.cta_text_color,
                }}
              >
                {parsedData.cta_text}
              </button>
            {/* </RevealOnScroll> */}
          </div>
          </RevealOnScroll>
        ) : null}
      </div>
    </section>
  );
};

export default Comparison1;
